"use client";

import { createClient } from "../../utils/supabase/client";
import { SUPABASE_TABLES } from "../supabase/supabaseDBConstants";
import { User } from "@supabase/auth-js";
import { SupabaseUser, CompanyUser, Company, CompanyUserUpdate, CompanyUserInsert } from "../supabase/types";

export type CompanyUserWithProfile = CompanyUser & {
  user: SupabaseUser;
};

export const usersApi = {
  async getUserInformation(authUser: User): Promise<SupabaseUser> {
    const supabase = createClient();
    
    // Retry logic with delay
    const maxRetries = 3;
    const retryDelay = 1000; // Start with 1 second delay

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Check if user already exists
        const { data: existingUser, error: fetchError } = await supabase
          .from(SUPABASE_TABLES.USERS)
          .select("*")
          .eq("id", authUser.id)
          .single();

        if (fetchError && fetchError.code !== "PGRST116") {
          // PGRST116 is "not found" which is expected for new users
          if (fetchError.code === '42501' && attempt < maxRetries) {
            console.log(`Permission denied getting user info, attempt ${attempt}/${maxRetries}, retrying...`);
            await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
            continue;
          }
          throw new Error(`Error fetching user: ${fetchError.message}`);
        }

        if (existingUser) {
          return existingUser;
        }

        // Create new user if doesn't exist
        const newUser: SupabaseUser = {
          id: authUser.id,
          email: authUser.email || "",
          display_name: authUser.user_metadata?.display_name || authUser.email?.split("@")[0] || null,
          photo_url: authUser.user_metadata?.avatar_url || null,
          previously_selected_company_id: null,
          microsoft_linked: authUser.app_metadata?.provider === "azure" || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          deleted_at: null,
        };

        const { data: createdUser, error: createError } = await supabase
          .from(SUPABASE_TABLES.USERS)
          .insert(newUser)
          .select()
          .single();

        if (createError) {
          if (createError.code === '42501' && attempt < maxRetries) {
            console.log(`Permission denied creating user, attempt ${attempt}/${maxRetries}, retrying...`);
            await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
            continue;
          }
          throw new Error(`Error creating user: ${createError.message}`);
        }

        return createdUser;
      } catch (error) {
        if (attempt === maxRetries) {
          throw error;
        }
        console.log(`Error in getUserInformation attempt ${attempt}/${maxRetries}:`, error);
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
      }
    }

    throw new Error("Failed to get user information after all retries");
  },

  async getUserCompanies(userId: string): Promise<{ companyUsers: CompanyUser[], companies: Company[] }> {
    const supabase = createClient();

    // Retry logic with delay
    const maxRetries = 3;
    const retryDelay = 1000; // Start with 1 second delay

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log(`Attempt ${attempt}/${maxRetries}: User not authenticated yet, waiting...`);
        
        if (attempt < maxRetries) {
          // Wait before retrying with exponential backoff
          await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
          continue;
        }
        
        console.error("User not authenticated after all retries when fetching user companies");
        return { companyUsers: [], companies: [] };
      }

      // User is authenticated, try to fetch company data
      const { data: companyUsers, error } = await supabase
        .from(SUPABASE_TABLES.COMPANY_USERS)
        .select("*, company:companies(*)")
        .eq("user_id", userId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) {
        console.error(`Attempt ${attempt}/${maxRetries}: Error fetching user companies:`, error);
        
        // If it's a permission error and we have retries left, wait and retry
        if (error.code === '42501' && attempt < maxRetries) {
          console.log(`Permission denied, retrying in ${retryDelay * attempt}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
          continue;
        }
        
        // For other errors or last attempt, return empty
        return { companyUsers: [], companies: [] };
      }

      // Success! Process and return the data
      // Extract companies and company users
      const companies: Company[] = [];
      const cleanCompanyUsers: CompanyUser[] = [];

      companyUsers?.forEach((item) => {
        const { company, ...companyUser } = item;
        if (company) {
          companies.push(company as Company);
          cleanCompanyUsers.push(companyUser as CompanyUser);
        }
      });

      return {
        companyUsers: cleanCompanyUsers,
        companies,
      };
    }

    // Fallback (shouldn't reach here)
    return { companyUsers: [], companies: [] };
  },

  async updateUserPreviouslySelectedCompany(userId: string, companyId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.USERS)
      .update({
        previously_selected_company_id: companyId,
        updated_at: new Date().toISOString(),
      })
      .eq("id", userId);

    if (error) {
      throw new Error("Error updating user previously selected company");
    }
  },

  async createNewUserRequest(authUser: User, companyId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.NEW_USER_REQUESTS)
      .insert({
        user_id: authUser.id,
        company_id: companyId,
        user_email: authUser.email || "",
        status: "pending",
        request_date: new Date().toISOString(),
      });

    if (error) {
      throw new Error("Error creating user request");
    }
  },

  /**
   * Get all users in a company (for managers and admins)
   * This will return all company users with their user details
   */
  async getCompanyUsers(companyId: string, userRole: string): Promise<CompanyUserWithProfile[]> {
    // Only managers and admins can view other company users
    if (userRole !== 'admin' && userRole !== 'manager') {
      return [];
    }

    const supabase = createClient();

    // Retry logic with delay
    const maxRetries = 3;
    const retryDelay = 1000; // Start with 1 second delay

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.log(`Attempt ${attempt}/${maxRetries}: User not authenticated yet when fetching company users, waiting...`);
        
        if (attempt < maxRetries) {
          // Wait before retrying with exponential backoff
          await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
          continue;
        }
        
        console.error("User not authenticated after all retries when fetching company users");
        return [];
      }

      // User is authenticated, try to fetch data
      const { data, error } = await supabase
        .from(SUPABASE_TABLES.COMPANY_USERS)
        .select(
          `
            *,
            user: user_id(*)
          `
        )
        .eq("company_id", companyId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) {
        console.error(`Attempt ${attempt}/${maxRetries}: Error fetching company users:`, error);
        
        // If it's a permission error and we have retries left, wait and retry
        if (error.code === '42501' && attempt < maxRetries) {
          console.log(`Permission denied, retrying in ${retryDelay * attempt}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
          continue;
        }
        
        // For other errors or last attempt, return empty
        return [];
      }

      // Success! Return the data
      return data || [];
    }

    // Fallback (shouldn't reach here)
    return [];
  },

  /**
   * Get current company information
   * Fetches the company details from the companies table
   */
  async getCurrentCompany(companyId: string): Promise<Company | null> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.COMPANIES)
      .select("*")
      .eq("id", companyId)
      .is("deleted_at", null)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        // No company found
        return null;
      }
      console.error("Error fetching current company:", error);
      throw new Error("Error fetching current company");
    }

    return data;
  },

  /**
   * Get multiple users by their IDs
   * Fetches user profiles for a list of user IDs
   */
  async getUsersProfileByIds(userIds: string[]): Promise<SupabaseUser[]> {
    if (!userIds || userIds.length === 0) {
      return [];
    }

    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.USERS)
      .select("*")
      .in("id", userIds)
      .is("deleted_at", null)
      .order("display_name", { ascending: true });

    if (error) {
      console.error("Error fetching users by IDs:", error);
      throw new Error("Error fetching users by IDs");
    }

    return data || [];
  },

  /**
   * Add a company to user's available companies
   */
  async addCompanyToUser(request: CompanyUserInsert): Promise<void> {
    const supabase = createClient();

    // First, check if the company user relationship already exists
    const { data: existing } = await supabase
      .from(SUPABASE_TABLES.COMPANY_USERS)
      .select("*")
      .eq("user_id", request.user_id)
      .eq("company_id", request.company_id)
      .single();

    if (existing) {
      // Already exists, just return
      return;
    }

    // Create new company user relationship
    const { error } = await supabase
      .from(SUPABASE_TABLES.COMPANY_USERS)
      .insert(request);

    if (error) {
      console.error("Error adding company to user:", error);
      throw new Error("Error adding company to user");
    }
  },

  /**
   * Remove a company from user's available companies
   */
  async removeCompanyUser(userId: string, companyId: string): Promise<void> {
    const supabase = createClient();

    // Soft delete the company user relationship
    const { error } = await supabase
      .from(SUPABASE_TABLES.COMPANY_USERS)
      .update({
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("user_id", userId)
      .eq("company_id", companyId);

    if (error) {
      console.error("Error removing company from user:", error);
      throw new Error("Error removing company from user");
    }
  },

  async updateCompanyUser(userId: string, updates: CompanyUserUpdate): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.COMPANY_USERS)
      .update(updates)
      .eq("user_id", userId);

    if (error) {
      console.error("Error updating company user:", error);
      throw new Error("Error updating company user");
    }
  },

  /**
   * Get a single user by their ID
   */
  async getUserById(userId: string): Promise<SupabaseUser | null> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.USERS)
      .select("*")
      .eq("id", userId)
      .is("deleted_at", null)
      .single();

    if (error) {
      if (error.code === "PGRST116") {
        // User not found
        return null;
      }
      console.error("Error fetching user by ID:", error);
      return null;
    }

    return data;
  }
};